/* NegotiateModal — lightweight "Request quote / negotiate price" dialog.
 *
 * Pops up from any product card or the detail dialog in /buyer/catalog.
 * Pre-fills product context (sku, name, seller) so the buyer only has to
 * say what they're asking for: target price, target qty, target terms,
 * and a short message. POSTs to /api/rfq (existing endpoint, extended
 * with the optional product_* and target_terms fields in iter Feb 2026).
 */
import { useEffect, useState } from "react";
import api, { money } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "./ui/dialog";
import { toast } from "sonner";
import { MessageSquare, Send, Package, Building2 } from "lucide-react";

const TERM_OPTIONS = [
  { v: "",       l: "No preference" },
  { v: "net30",  l: "Net-30" },
  { v: "net60",  l: "Net-60" },
  { v: "net90",  l: "Net-90" },
  { v: "net120", l: "Net-120" },
];

export default function NegotiateModal({ open, onClose, product }) {
  const [qty, setQty] = useState(product?.moq || 100);
  const [price, setPrice] = useState("");
  const [terms, setTerms] = useState("");
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);

  // Reset form when a different product is opened
  useEffect(() => {
    if (open && product) {
      setQty(product.moq || 100);
      setPrice("");
      setTerms("");
      setMsg("");
    }
  }, [open, product]);

  if (!product) return null;

  const submit = async () => {
    if (!qty || qty < 1) return toast.error("Target quantity is required");
    setBusy(true);
    try {
      const targetPrice = parseFloat(price);
      const parts = [
        msg ? msg.trim() : null,
        terms ? `Preferred terms: ${terms.toUpperCase().replace("NET", "Net-")}` : null,
        targetPrice ? `Target price: ${money(targetPrice)}/unit` : null,
        `Catalog price reference: ${money(product.price)}/unit · MOQ ${product.moq}`,
      ].filter(Boolean);
      await api.post("/rfq", {
        title: `Quote request — ${product.name}`,
        description: parts.join("\n"),
        target_qty: parseInt(qty) || 1,
        target_price: Number.isFinite(targetPrice) && targetPrice > 0 ? targetPrice : null,
        seller_ids: product.seller_id ? [product.seller_id] : [],
        product_id:   product.id,
        product_sku:  product.sku,
        product_name: product.name,
        target_terms: terms || null,
      });
      toast.success(`Quote request sent to ${product.seller_name}`);
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not send quote request");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg" data-testid="negotiate-modal">
        <DialogHeader>
          <DialogTitle className="display text-2xl tracking-tight inline-flex items-center gap-2">
            <MessageSquare className="w-5 h-5 text-foreground/60"/>
            Request quote
          </DialogTitle>
          <DialogDescription className="text-foreground/55">
            Send a private quote request to {product.seller_name}. They'll respond with their best price, MOQ and terms — no commitment until you accept.
          </DialogDescription>
        </DialogHeader>

        {/* Product context */}
        <div className="rounded-xl bg-secondary/50 border border-border p-3 flex items-center gap-3">
          <div className="w-12 h-12 rounded-lg bg-white border border-border overflow-hidden shrink-0">
            {product.image
              ? <img src={product.image} alt={product.name} className="w-full h-full object-cover"/>
              : <div className="w-full h-full grid place-items-center text-foreground/30"><Package className="w-5 h-5"/></div>}
          </div>
          <div className="min-w-0 flex-1">
            <div className="font-medium text-sm truncate">{product.name}</div>
            <div className="text-[11px] text-foreground/55 mt-0.5 inline-flex items-center gap-1.5 flex-wrap">
              <Building2 className="w-3 h-3"/>{product.seller_name}
              <span className="mx-1 text-foreground/30">·</span>
              <span className="mono">{product.sku}</span>
              <span className="mx-1 text-foreground/30">·</span>
              <span>Catalog {money(product.price)} / MOQ {product.moq}</span>
            </div>
          </div>
        </div>

        {/* Fields */}
        <div className="space-y-3 mt-2">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="neg-qty">Target quantity</Label>
              <Input id="neg-qty" type="number" min={1} value={qty}
                onChange={(e) => setQty(e.target.value)}
                className="mt-1.5 h-11 mono" data-testid="neg-qty"/>
            </div>
            <div>
              <Label htmlFor="neg-price">Target unit price (optional)</Label>
              <Input id="neg-price" type="number" step="0.01" min={0} value={price}
                onChange={(e) => setPrice(e.target.value)}
                placeholder="$" className="mt-1.5 h-11 mono" data-testid="neg-price"/>
            </div>
          </div>
          <div>
            <Label htmlFor="neg-terms">Preferred payment terms</Label>
            <select id="neg-terms" value={terms} onChange={(e) => setTerms(e.target.value)}
              className="mt-1.5 w-full h-11 rounded-md border border-input bg-white px-3 text-sm" data-testid="neg-terms">
              {TERM_OPTIONS.map(t => <option key={t.v} value={t.v}>{t.l}</option>)}
            </select>
          </div>
          <div>
            <Label htmlFor="neg-msg">Message to supplier</Label>
            <textarea id="neg-msg" value={msg} onChange={(e) => setMsg(e.target.value)}
              placeholder="Anything else they should know? Volume cadence, packaging, delivery window…"
              className="mt-1.5 w-full rounded-md border border-input bg-white p-3 text-sm h-24"
              data-testid="neg-msg"/>
          </div>
        </div>

        <Button onClick={submit} disabled={busy} data-testid="neg-submit"
          className="w-full h-11 rounded-full bg-foreground text-background hover:bg-foreground/90 mt-2">
          {busy ? "Sending…" : <><Send className="w-4 h-4 mr-1.5"/>Send quote request</>}
        </Button>
        <p className="text-[10px] text-center text-foreground/50">
          You can track quote responses in <span className="font-medium">Apps → RFQ</span>.
        </p>
      </DialogContent>
    </Dialog>
  );
}
